import { useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "../../../ui/Icon";
import { Art } from "../../../ui/Art";
import { learnerKey } from "../../../lib/learnerId";
import {
  wordArtId, clipFor as msClip, enClipFor, jawiClip, jawiAnswerPool, JAWI_LETTERS, kiraQuestion, kiraRound, NOMBOR, NOMBOR_EN, tambahQuestion, kiraItemsFor, warnaFor, gambarWarnaFor,
  type ColorPicture, type JawiLetter, type KiraRound,
} from "@jomcerdik/shared";
import { t, useLang } from "../../../lib/i18n";
import { playClip, playQueue, useAudioSource } from "../../../lib/bacaAudio";
import { Card, Screen, StudyGate, TopBar } from "../kit";
import { Burst, shuffle } from "../games/common";
import { Done, contentLang, isEn, useHear, useRecord } from "./TadikaPages";

/* Tadika games. Content is Bahasa Melayu by design; only the UI chrome is translated. */

const ANSWER_TONES = ["from-rose-400 to-pink-500", "from-amber-300 to-orange-400", "from-emerald-400 to-green-500", "from-sky-400 to-blue-500"];
const word = (text: string) => (isEn() ? enClipFor("word", text) : msClip("word", text));
const line = (text: string) => (isEn() ? enClipFor("line", text) : msClip("line", text));
const RIGHT = () => (isEn() ? "Right! Well done!" : "Betul! Pandai!");
const TRY = () => (isEn() ? "Try again." : "Cuba lagi.");
const BETUL = () => line(RIGHT());
const CUBA = () => line(TRY());
const nombor = () => (isEn() ? NOMBOR_EN : NOMBOR);

/** Big 2×2 colourful answer buttons. */
function Answers<T>({ items, label, state, onPick, big = true, className = "" }: {
  items: T[]; label: (x: T) => string; state: (x: T) => "idle" | "right" | "wrong" | "dim"; onPick: (x: T) => void; big?: boolean; className?: string;
}) {
  return (
    <div className="grid grid-cols-2 gap-3">
      {items.map((x, i) => {
        const st = state(x);
        return (
          <button key={label(x)} type="button" onClick={() => onPick(x)}
            className={`rounded-3xl bg-gradient-to-br ${ANSWER_TONES[i % 4]} py-5 font-black text-white shadow-md ring-4 transition active:scale-95 ${big ? "text-5xl" : "text-2xl"} ${className}
              ${st === "right" ? "ring-emerald-300 animate-tap" : st === "wrong" ? "ring-rose-300 animate-shake opacity-70" : st === "dim" ? "opacity-40 ring-transparent" : "ring-white/40"}`}>
            {label(x)}
          </button>
        );
      })}
    </div>
  );
}

/* ── Kira Gambar ─────────────────────────────────────────────────────────── */
const KIRA_ROUNDS = 10;
const STAGES = [
  { stage: 1 as const, label: "1 – 5", art: "mascot/girl-animals", tone: "from-amber-300 to-orange-400" },
  { stage: 2 as const, label: "1 – 10", art: "haiwan/ikan", tone: "from-sky-400 to-blue-500" },
  { stage: 3 as const, label: "+", art: "buah/epal", tone: "from-emerald-400 to-teal-500" },
];

export function KiraPage() {
  return <Screen><StudyGate><Kira /></StudyGate></Screen>;
}

function Kira() {
  const [stage, setStage] = useState<1 | 2 | 3 | null>(null);
  const [seed, setSeed] = useState(0);
  const appLang = useLang();
  useAudioSource(useMemo(() => [BETUL(), CUBA(), ...nombor().slice(1).map(word)], [appLang]));
  return (
    <>
      <TopBar back="/belajar/tadika" title={t("Kira Gambar")} subtitle={t("Kira gambar, kemudian tekan jawapan yang betul.")} />
      {stage === null ? (
        <div className="grid gap-3 sm:grid-cols-3">
          {STAGES.map((s) => (
            <button key={s.stage} type="button" onClick={() => setStage(s.stage)}
              className={`rounded-3xl bg-gradient-to-br ${s.tone} p-6 text-center text-white shadow-md transition hover:-translate-y-0.5`}>
              <Art id={s.art} alt="" className="mx-auto h-24 w-24" />
              <p className="mt-2 text-3xl font-black">{s.label}</p>
              <p className="text-sm font-bold opacity-90">{s.stage === 3 ? t("Tambah gambar") : t("Kira gambar")}</p>
            </button>
          ))}
        </div>
      ) : (
        <KiraGame key={`${seed}${appLang}`} stage={stage} onAgain={() => setSeed((x) => x + 1)} onDone={() => setStage(null)} />
      )}
    </>
  );
}

function KiraGame({ stage, onAgain, onDone }: { stage: 1 | 2 | 3; onAgain: () => void; onDone: () => void }) {
  const rounds = useMemo(() => {
    const out: KiraRound[] = [];
    for (let i = 0; i < KIRA_ROUNDS; i++) { let r = kiraRound(stage, Math.random, kiraItemsFor(contentLang())); while (out.length && out[out.length - 1]!.answer === r.answer && out[out.length - 1]!.item.name === r.item.name) r = kiraRound(stage, Math.random, kiraItemsFor(contentLang())); out.push(r); }
    return out;
  }, [stage]);
  const [i, setI] = useState(0);
  const [picked, setPicked] = useState<number[]>([]);
  const [solved, setSolved] = useState(false);
  const [score, setScore] = useState(0);
  const [burst, setBurst] = useState(0);
  const [counted, setCounted] = useState<number[]>([]);
  const record = useRecord(0, `kira-${stage}`);
  const hear = useHear();
  const r = rounds[i];
  const q = r ? (r.mode === "tambah" ? tambahQuestion(r.item) : kiraQuestion(r.item)) : "";
  if (!r) return <Done score={score} max={rounds.length} onAgain={onAgain} onDone={onDone} save={record} />;

  function pick(n: number) {
    if (solved || !r) return;
    if (n === r.answer) {
      setSolved(true); setBurst((b) => b + 1);
      if (picked.length === 0) setScore((s) => s + 1);
      playQueue([BETUL(), word(nombor()[n]!)], `${RIGHT()} ${nombor()[n]}`);
    } else {
      setPicked((p) => [...p, n]);
      playClip(CUBA(), TRY());
    }
  }
  function next() {
    const nr = rounds[i + 1];
    setI((x) => x + 1); setPicked([]); setSolved(false); setCounted([]);
    if (nr) { const nq = nr.mode === "tambah" ? tambahQuestion(nr.item) : kiraQuestion(nr.item); playClip(line(nq), nq); }
  }
  /** Tap a picture to count it aloud: 1, 2, 3… */
  function count(k: number) {
    if (counted.includes(k)) return;
    const nc = [...counted, k];
    setCounted(nc);
    playClip(word(nombor()[nc.length]!), nombor()[nc.length]!);
  }
  const group = (n: number, offset: number) => (
    <div className="flex max-w-[16rem] flex-wrap justify-center gap-1">
      {Array.from({ length: n }, (_, k) => {
        const idx = offset + k;
        const at = counted.indexOf(idx);
        return (
          <button key={idx} type="button" onClick={() => count(idx)} aria-label={r.item.name}
            className="relative animate-rise" style={{ animationDelay: `${k * 60}ms` }}>
            <Art id={r.item.art ?? wordArtId(r.item.name)} alt="" className="h-16 w-16 sm:h-20 sm:w-20" />
            {at >= 0 && <span className="absolute -right-1 -top-1 grid h-6 w-6 place-items-center rounded-full bg-violet-500 text-xs font-black text-white">{at + 1}</span>}
          </button>
        );
      })}
    </div>
  );

  return (
    <Card className="space-y-4 text-center">
      <Burst show={burst} />
      <div className="flex items-center justify-between text-xs font-bold text-slate-400">
        <span>{i + 1}/{rounds.length}</span><span className="inline-flex items-center gap-1"><Icon name="star" className="h-4 w-4 text-amber-400" />{score}</span>
      </div>
      <div className="flex items-center justify-center gap-2">
        <p className="text-2xl font-extrabold text-slate-900">{q}</p>
        {hear && <button type="button" onClick={() => playClip(line(q), q)} aria-label={t("Dengar")} className="grid h-10 w-10 place-items-center rounded-full bg-emerald-500 text-white"><Icon name="speaker" className="mr-1 inline h-4 w-4" /></button>}
      </div>
      <div key={i} className="flex min-h-[9rem] flex-wrap items-center justify-center gap-3 rounded-3xl bg-gradient-to-b from-sky-50 to-white p-4 ring-1 ring-sky-100 dark:from-slate-100 dark:to-slate-50">
        {group(r.a, 0)}
        {r.mode === "tambah" && <><span className="text-4xl font-black text-emerald-500">+</span>{group(r.b, 100)}</>}
      </div>
      <p className="text-xs text-slate-400">{t("Tekan gambar untuk mengira: satu, dua, tiga…")}</p>
      <Answers items={r.options} label={(n) => String(n)} onPick={pick}
        state={(n) => (solved ? (n === r.answer ? "right" : "dim") : picked.includes(n) ? "wrong" : "idle")} />
      {solved && <button type="button" onClick={next} className="w-full rounded-full bg-violet-500 py-3 text-lg font-extrabold text-white shadow-md">{t("Seterusnya")} ›</button>}
    </Card>
  );
}

/* ── Celik Jawi ──────────────────────────────────────────────────────────── */
type JawiMode = "kenal" | "kuiz" | "cari";
const JAWI_FONT = "font-['Noto_Naskh_Arabic',_'Amiri',_serif]";

export function JawiPage() {
  return <Screen><StudyGate><Jawi /></StudyGate></Screen>;
}

function useJawiFont() {
  useEffect(() => {
    if (document.getElementById("jawi-font")) return;
    const l = document.createElement("link");
    l.id = "jawi-font"; l.rel = "stylesheet";
    l.href = "https://fonts.googleapis.com/css2?family=Noto+Naskh+Arabic:wght@500;700&display=swap";
    document.head.appendChild(l);
  }, []);
}

function Jawi() {
  useJawiFont();
  const [mode, setMode] = useState<JawiMode>("kenal");
  const [seed, setSeed] = useState(0);
  const audio = useAudioSource(useMemo(() => JAWI_LETTERS.map(jawiClip), []));
  const tabs: { id: JawiMode; label: string; e: string }[] = [
    { id: "kenal", label: t("Kenal huruf"), e: "book" }, { id: "kuiz", label: t("Huruf apa?"), e: "star" },
    { id: "cari", label: t("Cari huruf"), e: "check" },
  ];
  return (
    <>
      <TopBar back="/belajar/tadika" title={t("Celik Jawi")} subtitle={`${JAWI_LETTERS.length} huruf mengikut carta Belajar Huruf JAWI. Tekan huruf untuk dengar namanya.`} />
      {audio !== "audio" && <p role="status" className="mb-3 text-sm text-slate-600">Audio huruf sedang disediakan dan disemak.</p>}
      <div className="mb-4 grid grid-cols-3 gap-2">
        {tabs.map((x) => (
          <button key={x.id} type="button" aria-pressed={mode === x.id} onClick={() => { setMode(x.id); setSeed((s) => s + 1); }}
            className={`rounded-2xl px-1 py-2 text-center text-xs font-extrabold shadow-sm ${mode === x.id ? "bg-emerald-500 text-white" : "bg-card text-slate-700 ring-1 ring-black/5"}`}>
            <Icon name={x.e} className="mx-auto mb-1 h-6 w-6" />{x.label}
          </button>
        ))}
      </div>
      {mode === "kenal" && <JawiChart />}
      {mode === "kuiz" && <JawiQuiz key={seed} kind="kuiz" onAgain={() => setSeed((s) => s + 1)} onDone={() => setMode("kenal")} />}
      {mode === "cari" && <JawiQuiz key={seed} kind="cari" onAgain={() => setSeed((s) => s + 1)} onDone={() => setMode("kenal")} />}
    </>
  );
}

const LETTER_TONES = ["bg-rose-100 text-rose-700", "bg-amber-100 text-amber-700", "bg-emerald-100 text-emerald-700", "bg-sky-100 text-sky-700", "bg-violet-100 text-violet-700", "bg-pink-100 text-pink-700"];

function JawiChart() {
  const [pop, setPop] = useState<string | null>(null);
  return (
    <div className="grid grid-cols-6 gap-2" dir="rtl">
      {JAWI_LETTERS.map((l, i) => (
        <button key={l.j + l.name} type="button" data-audio-key={jawiClip(l).key} onClick={() => { setPop(l.j + l.name); playClip(jawiClip(l)); }}
          className={`rounded-2xl p-0.5 text-center shadow-sm ring-1 ring-black/5 sm:p-2 ${LETTER_TONES[i % LETTER_TONES.length]} ${pop === l.j + l.name ? "animate-tap" : ""}`}>
          <span className={`flex h-24 items-center justify-center text-3xl leading-[2] sm:text-5xl ${JAWI_FONT}`}>{l.j}</span>
          <span className="block whitespace-nowrap text-[10px] font-extrabold sm:text-xs" dir="ltr">{l.name}</span>
        </button>
      ))}
    </div>
  );
}

function JawiQuiz({ kind, onAgain, onDone }: { kind: "kuiz" | "cari"; onAgain: () => void; onDone: () => void }) {
  const pool = useMemo(() => JAWI_LETTERS.filter((l) => l.quiz), []);
  const deck = useMemo(() => shuffle(pool).slice(0, 10), [pool]);
  const [i, setI] = useState(0);
  const [wrong, setWrong] = useState<string[]>([]);
  const [solved, setSolved] = useState(false);
  const [score, setScore] = useState(0);
  const [burst, setBurst] = useState(0);
  const record = useRecord(0, `jawi-${kind}`);
  const hear = useHear();
  const cur = deck[i];
  const opts = useMemo(() => (cur ? shuffle([cur, ...shuffle(jawiAnswerPool(cur)).slice(0, 3)]) : []), [cur]);
  if (!cur) return <Done score={score} max={deck.length} onAgain={onAgain} onDone={onDone} save={record} />;

  function pick(l: JawiLetter) {
    if (solved || !cur) return;
    if (l.j === cur.j) {
      setSolved(true); setBurst((b) => b + 1);
      if (!wrong.length) setScore((s) => s + 1);
      playQueue([BETUL(), jawiClip(cur)], `Betul! ${cur.name}`);
    } else { setWrong((w) => [...w, l.j]); playClip(CUBA(), "Cuba lagi."); }
  }
  function next() {
    const n = deck[i + 1];
    setI((x) => x + 1); setWrong([]); setSolved(false);
    if (n && kind === "cari") playClip(jawiClip(n));
  }
  const state = (l: JawiLetter) => (solved ? (l.j === cur.j ? "right" : "dim") : wrong.includes(l.j) ? "wrong" : "idle");

  return (
    <Card className="space-y-4 text-center">
      <Burst show={burst} />
      <div className="flex items-center justify-between text-xs font-bold text-slate-400"><span>{i + 1}/{deck.length}</span><span className="inline-flex items-center gap-1"><Icon name="star" className="h-4 w-4 text-amber-400" />{score}</span></div>
      {kind === "kuiz" ? (
        <>
          <p className="text-xl font-extrabold text-slate-900">{t("Huruf apakah ini?")}</p>
          <p className={`mx-auto grid h-40 w-40 place-items-center rounded-3xl bg-gradient-to-br from-emerald-100 to-sky-100 text-8xl text-slate-900 ${JAWI_FONT}`}>{cur.j}</p>
          <Answers items={opts} label={(l) => l.name} state={state} onPick={pick} big={false} className="py-6 text-3xl" />
        </>
      ) : (
        <>
          <p className="text-xl font-extrabold text-slate-900">{t("Cari huruf ini:")} <span className="rounded-xl bg-amber-100 px-3 py-1 text-amber-800">{cur.name}</span></p>
          {hear && <button type="button" onClick={() => playClip(jawiClip(cur), cur.name)} className="rounded-full bg-emerald-500 px-4 py-1.5 text-sm font-bold text-white"><Icon name="speaker" className="mr-1 inline h-4 w-4" /> {t("Dengar")}</button>}
          <Answers items={opts} label={(l) => l.j} state={state} onPick={pick} className={`text-6xl leading-[1.5] ${JAWI_FONT}`} />
        </>
      )}
      {solved && <button type="button" onClick={next} className="w-full rounded-full bg-violet-500 py-3 text-lg font-extrabold text-white shadow-md">{t("Seterusnya")} ›</button>}
    </Card>
  );
}

/* ── Mewarna ─────────────────────────────────────────────────────────────── */
export function WarnaPage() {
  return <Screen><StudyGate><Warna /></StudyGate></Screen>;
}

function Warna() {
  const [pic, setPic] = useState<ColorPicture | null>(null);
  const appLang = useLang();
  const WARNA = useMemo(() => warnaFor(contentLang()), [appLang]);
  const GAMBAR_WARNA = useMemo(() => gambarWarnaFor(contentLang()), [appLang]);
  useAudioSource(useMemo(() => WARNA.map((w) => word(w.name)), [WARNA]));
  return (
    <>
      <TopBar back="/belajar/tadika" title={t("Mewarna")} subtitle={t("Pilih warna, kemudian tekan bahagian gambar.")} />
      {pic ? <Colouring key={pic.id} pic={pic} onBack={() => setPic(null)} /> : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {GAMBAR_WARNA.map((p, i) => (
            <button key={p.id} type="button" onClick={() => setPic(p)} className={`rounded-3xl bg-gradient-to-br ${ANSWER_TONES[i % 4]} p-5 text-center text-white shadow-md transition hover:-translate-y-0.5`}>
              <Art id={wordArtId(p.id)} alt={p.name} className="mx-auto h-24 w-24" />
              <span className="mt-1 block text-xl font-extrabold">{p.name}</span>
            </button>
          ))}
        </div>
      )}
    </>
  );
}

type Fills = Record<string, string>;
const loadFills = (id: string): Fills => { try { return JSON.parse(localStorage.getItem(learnerKey(`warna.${id}`)) ?? "{}") as Fills; } catch { return {}; } };

function Colouring({ pic, onBack }: { pic: ColorPicture; onBack: () => void }) {
  const appLang = useLang();
  const WARNA = useMemo(() => warnaFor(contentLang()), [appLang]);
  const [color, setColor] = useState(WARNA[0]!.hex);
  const [fills, setFills] = useState<Fills>(() => loadFills(pic.id));
  const [history, setHistory] = useState<Fills[]>([]);
  const [burst, setBurst] = useState(0);
  const [tapped, setTapped] = useState<string | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const record = useRecord(0, `mewarna-${pic.id}`);
  const savedOnce = useRef(false);
  useEffect(() => { try { localStorage.setItem(learnerKey(`warna.${pic.id}`), JSON.stringify(fills)); } catch { /* ignore */ } }, [fills, pic.id]);

  const filledCount = pic.parts.filter((p) => fills[p.id]).length;
  function paint(id: string) {
    setHistory((h) => [...h.slice(-30), fills]);
    const next = { ...fills, [id]: color };
    setFills(next);
    setTapped(id); setTimeout(() => setTapped(null), 300);
    if (pic.parts.every((p) => next[p.id]) && !savedOnce.current) {
      savedOnce.current = true; setBurst((b) => b + 1); playClip(BETUL(), RIGHT());
      record(pic.parts.length, pic.parts.length);
    }
  }
  function choose(hex: string, name: string) { setColor(hex); playClip(word(name), name); }
  function undo() { setHistory((h) => { const last = h[h.length - 1]; if (last) setFills(last); return h.slice(0, -1); }); }
  function clear() { setHistory((h) => [...h, fills]); setFills({}); savedOnce.current = false; }
  async function save() {
    const svg = svgRef.current;
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(xml)}`;
    await img.decode().catch(() => undefined);
    const c = document.createElement("canvas");
    c.width = 800; c.height = 800;
    const ctx = c.getContext("2d");
    if (!ctx) return;
    ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, 800, 800); ctx.drawImage(img, 0, 0, 800, 800);
    const a = document.createElement("a");
    a.href = c.toDataURL("image/png"); a.download = `JomCerdik-${pic.id}.png`; a.click();
  }

  return (
    <div className="space-y-3">
      <Burst show={burst} emoji={["🎨", "⭐", "🌈", "✨"]} />
      <div className="flex items-center justify-between">
        <button type="button" onClick={onBack} className="rounded-full bg-card px-4 py-1.5 text-sm font-bold text-slate-700 ring-1 ring-black/10">‹ {t("Gambar lain")}</button>
        <span className="text-xs font-bold text-slate-400">{filledCount}/{pic.parts.length}</span>
      </div>
      <Card className="p-2">
        <svg ref={svgRef} viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" className="mx-auto block w-full max-w-md touch-manipulation select-none" role="img" aria-label={pic.name}>
          <rect x="0" y="0" width="200" height="200" fill="#ffffff" />
          {pic.parts.map((p) => (
            <path key={p.id} d={p.d} fill={fills[p.id] ?? "#ffffff"} stroke="#1f2937" strokeWidth={2} strokeLinejoin="round"
              onClick={() => paint(p.id)} className="cursor-pointer transition-[fill] duration-200"
              style={tapped === p.id ? { filter: "brightness(1.15)" } : undefined} />
          ))}
          {(pic.lines ?? []).map((d, i) => <path key={i} d={d} fill="none" stroke="#1f2937" strokeWidth={2} strokeLinecap="round" pointerEvents="none" />)}
        </svg>
      </Card>
      <div className="grid grid-cols-6 gap-2">
        {WARNA.map((w) => (
          <button key={w.hex} type="button" data-audio-key={word(w.name).key} aria-label={w.name} aria-pressed={color === w.hex} onClick={() => choose(w.hex, w.name)}
            className={`mx-auto h-12 w-12 rounded-full shadow-md ring-2 ring-black/10 transition ${color === w.hex ? "scale-110 ring-4 ring-violet-500" : ""}`}
            style={{ background: w.hex }} />
        ))}
      </div>
      <div className="grid grid-cols-3 gap-2">
        <button type="button" onClick={undo} disabled={!history.length} className="rounded-2xl bg-card py-3 text-sm font-extrabold text-slate-700 shadow-sm ring-1 ring-black/5 disabled:opacity-40">{t("Undur")}</button>
        <button type="button" onClick={clear} className="rounded-2xl bg-card py-3 text-sm font-extrabold text-slate-700 shadow-sm ring-1 ring-black/5">🧽 {t("Padam semua")}</button>
        <button type="button" onClick={save} className="rounded-2xl bg-gradient-to-r from-pink-500 to-orange-400 py-3 text-sm font-extrabold text-white shadow-md">💾 {t("Simpan gambar")}</button>
      </div>
    </div>
  );
}


